// The composed MONEY-BEARING Booking per-artist dashboard read — ONE band-scoped query for
// the KPI strip + routing + active deals + recent settlements + to-dos. Keyed ['band', bandId, 'booking',
// 'dashboard'] so an artist switch SCORCHES it (switchRosterArtist). The hook calls the BAND-DIRECT path;
// under the roster shell the org-lens middleware (app/orgLens.ts) splices `/orgs/{orgId}` so it resolves the
// `/orgs/{orgId}/bands/{bandId}/booking/dashboard` mirror. MONEY-BEARING — the route requires money:full AND
// money:lifetime, so the caller SELF-GATES on `money:lifetime` (via the `enabled` flag) and a money-free seat NEVER
// fires the request (the A3 rule client-side; the server 403s it regardless). NOT enveloped — returns the DTO directly.
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandQuery } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type BookingArtistDashboard = components["schemas"]["BookingArtistDashboard"];

/** GET the composed money-bearing Booking per-artist dashboard. `enabled` should be the `money:lifetime`
 * capability so a money-free viewer never calls the endpoint (self-gate). `staleTime` ~60s (money is authoritative
 * live from settlement/finances; this is a summary). */
export function useBookingArtistDashboard(enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<BookingArtistDashboard>(
    ["booking", "dashboard"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/booking/dashboard", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled, staleTime: 60_000 },
  );
}
